/**
 * Work a reload would lose that no screen registers itself
 * (plan/portal-final/INTERFACES.md section 8): writes still in flight
 * (any fetch or XHR that is not GET or HEAD, which covers file uploads) and
 * typing on the page now open. Typing is forgotten on a route change,
 * because a single-page navigation discards the form it was typed into in
 * any case; it matters only when a hidden tab comes back and would reload
 * the page the user is still on.
 */

let inflight = 0;
let typed = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function isWrite(method: string | undefined): boolean {
  const verb = (method ?? 'GET').toUpperCase();
  return verb !== 'GET' && verb !== 'HEAD';
}

function track<T>(work: Promise<T>): Promise<T> {
  inflight += 1;
  notify();
  return work.finally(() => {
    inflight -= 1;
    notify();
  });
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** Starts tracking. Returns the uninstall. `onChange` fires whenever activity may have changed. */
export function installActivityTracking(onChange: () => void): () => void {
  listeners.add(onChange);
  const originalFetch = window.fetch;
  const wrappedFetch: typeof window.fetch = (input, init) => {
    const method = init?.method ?? (input instanceof Request ? input.method : undefined);
    const call = originalFetch.call(window, input, init);
    return isWrite(method) ? track(call) : call;
  };
  window.fetch = wrappedFetch;

  const proto = XMLHttpRequest.prototype;
  const originalOpen = proto.open;
  const originalSend = proto.send;
  const methods = new WeakMap<XMLHttpRequest, string>();
  proto.open = function open(this: XMLHttpRequest, method: string, ...rest: unknown[]) {
    methods.set(this, method);
    return (originalOpen as (...args: unknown[]) => void).call(this, method, ...rest);
  } as typeof proto.open;
  proto.send = function send(this: XMLHttpRequest, body?: Parameters<XMLHttpRequest['send']>[0]) {
    if (isWrite(methods.get(this))) {
      void track(
        new Promise<void>((resolve) => this.addEventListener('loadend', () => resolve(), { once: true })),
      );
    }
    return originalSend.call(this, body);
  };

  const onInput = (event: Event) => {
    if (!typed && isTypingTarget(event.target)) {
      typed = true;
      notify();
    }
  };
  document.addEventListener('input', onInput, true);

  return () => {
    listeners.delete(onChange);
    if (window.fetch === wrappedFetch) window.fetch = originalFetch;
    proto.open = originalOpen;
    proto.send = originalSend;
    document.removeEventListener('input', onInput, true);
  };
}

/** Forgets typing on the page that was just left. */
export function routeLeft(): void {
  if (typed) {
    typed = false;
    notify();
  }
}

export function hasActivity(): boolean {
  return inflight > 0 || typed;
}
